<template>
  <div class="city-area-container">
    <el-row :gutter="16">
      <!-- 左侧地区树：保留原有 add / edit / delete 入口与节点联动 -->
      <el-col :xs="24" :sm="24" :md="7" :lg="6">
        <city-area-tree @switchCityAreaTree="handleSwitchCityAreaTree" @handleRefresh="handleRefresh" />
      </el-col>

      <el-col :xs="24" :sm="24" :md="17" :lg="18">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('cityArea.page.title')" :subtitle="$t('cityArea.page.subtitle')" />

        <!-- 地区详情卡片：字段卡片化，替代原来的 label/value 平铺 -->
        <div class="ds-panel">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('cityArea.detailTitle') }}</span>
            <el-tag v-if="cityAreaInfo.areaCode" type="info" size="small" class="ds-panel__tag">
              {{ resolveAreaLevel(cityAreaInfo) }}
            </el-tag>
          </div>

          <div class="ds-detail">
            <div class="ds-detail__item">
              <div class="ds-detail__label">{{ $t('cityArea.areaName') }}</div>
              <div class="ds-detail__value">{{ cityAreaInfo.areaName || '-' }}</div>
            </div>
            <div class="ds-detail__item">
              <div class="ds-detail__label">{{ $t('cityArea.areaCode') }}</div>
              <div class="ds-detail__value ds-num">{{ cityAreaInfo.areaCode || '-' }}</div>
            </div>
            <div class="ds-detail__item">
              <div class="ds-detail__label">{{ $t('cityArea.areaLevel') }}</div>
              <div class="ds-detail__value">{{ resolveAreaLevel(cityAreaInfo) }}</div>
            </div>
            <div class="ds-detail__item">
              <div class="ds-detail__label">{{ $t('cityArea.parentAreaCode') }}</div>
              <div class="ds-detail__value ds-num">{{ cityAreaInfo.parentAreaCode || '-' }}</div>
            </div>
            <div class="ds-detail__item">
              <div class="ds-detail__label">{{ $t('cityArea.parentAreaName') }}</div>
              <div class="ds-detail__value">{{ cityAreaInfo.parentAreaName || '-' }}</div>
            </div>
            <div class="ds-detail__item">
              <div class="ds-detail__label">{{ $t('cityArea.createTime') }}</div>
              <div class="ds-detail__value ds-num">{{ formatDateTimeSec(cityAreaInfo.createTime) }}</div>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import CityAreaTree from '@/components/community/cityAreaTree'

import { getAreaDetail } from '@/api/community/cityAreaApi'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'

export default {
  name: 'CityAreaList',
  components: {
    CityAreaTree,
    DsPageHeader
  },
  data() {
    return {
      cityAreaInfo: {
        id: '',
        areaCode: '',
        areaName: '',
        areaLevel: '',
        parentAreaCode: '',
        parentAreaName: '',
        createTime: ''
      }
    }
  },
  methods: {
    handleSwitchCityAreaTree(params) {
      this.cityAreaInfo.id = params.id
      this.cityAreaInfo.areaCode = params.areaCode
      this.cityAreaInfo.areaName = params.areaName
      this.getAreaDetail()
    },
    async getAreaDetail() {
      try {
        const params = {
          id: this.cityAreaInfo.id,
          page: 1,
          row: 1
        }
        const { areas } = await getAreaDetail(params)
        if (areas && areas.length > 0) {
          this.cityAreaInfo = { ...this.cityAreaInfo, ...areas[0] }
        }
      } catch (error) {
        console.error('查询地区详情失败:', error)
        this.$message.error(this.$t('common.fetchError'))
      }
    },
    handleRefresh() {
      this.getAreaDetail()
    },
    /**
     * 地区层级文案：101 省级 / 202 市州 / 303 区县 / 其它为普通地区
     */
    resolveAreaLevel(info) {
      if (!info) return '-'
      if (info.areaLevel === '101') return this.$t('cityArea.province')
      if (info.areaLevel === '202') return this.$t('cityArea.city')
      if (info.areaLevel === '303') return this.$t('cityArea.district')
      return this.$t('cityArea.area')
    },
    /**
     * 创建时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.city-area-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ds-spacing-md);
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    background: var(--ds-gray-50);
    border-bottom: 1px solid var(--ds-gray-300);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 详情字段网格 */
.ds-detail {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--ds-spacing-md);
  padding: var(--ds-spacing-lg);

  &__item {
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    border: 1px solid var(--ds-gray-200);
    border-radius: var(--ds-radius-sm);
    background: var(--ds-gray-50);
  }

  &__label {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    margin-bottom: var(--ds-spacing-xs);
  }

  &__value {
    font-size: var(--ds-font-size-base);
    color: var(--ds-gray-800);
    word-break: break-all;
    /* ds-num 默认右对齐（用于表格数值列），此处为标签-值布局，改回左对齐 */
    text-align: left;
  }
}
</style>
